/**
 * Shared visual primitives for the XAES entry sequence — the
 * Mission Control opening screen and the LoginPage are two states of
 * the same scene, so they share the title + plasma orb here.
 *
 *   - `<XaesTitle />` renders the four-letter `XAES` wordmark with the
 *     scanline overlay and the per-letter fly-in (`xaes-fly-in`,
 *     `xaes-title-glow`). Ported from `_legacy/mc.html` lines 82-112.
 *   - `<PlasmaOrb />` renders the haloed `PlasmaCanvas` block that
 *     pulses below the title (legacy `_legacy/mc.html` line 104 +
 *     `xaes-orb-pulse` keyframe). Size matches the legacy 9 vw / 5 vw
 *     min/max clamp so the orb scales with the viewport.
 *
 * Both are pure presentational and have no business logic, which lets
 * the two callers (OpeningScreen + LoginPage) compose the rest of the
 * page however they need.
 */

import type { CSSProperties } from "react";
import { PlasmaCanvas } from "@/components/fx/PlasmaCanvas";

const LETTERS: Array<{ char: string; flyFrom: string }> = [
  { char: "X", flyFrom: "-12vw" },
  { char: "A", flyFrom: "-4vw" },
  { char: "E", flyFrom: "4vw" },
  { char: "S", flyFrom: "12vw" },
];

type Size = "default" | "compact";

/**
 * Per-size token table. `default` matches the legacy MC opening
 * (`_legacy/mc.html` line 88) values. `compact` is roughly 55 % of
 * the default and is used by the sign-in screen so the title + orb +
 * form all fit inside the dark TV plate visible in `bg-mission.jpg`.
 */
/* Clamped rather than pure vw: the legacy values alone render the
   wordmark at ~5px on a phone and ~100px on a 4K wall display. The vw
   term still drives the middle, the bounds keep both ends legible. */
const TITLE_SIZES: Record<
  Size,
  { font: string; letter: string; padLeft: string }
> = {
  default: {
    font: "clamp(30px, 2.6vw, 64px)",
    letter: "clamp(12px, 1.2vw, 28px)",
    padLeft: "clamp(12px, 1.2vw, 28px)",
  },
  compact: {
    font: "clamp(22px, 1.45vw, 36px)",
    letter: "clamp(8px, 0.7vw, 16px)",
    padLeft: "clamp(8px, 0.7vw, 16px)",
  },
};

const ORB_SIZES: Record<
  Size,
  {
    width: string;
    minW: number;
    maxW: number;
    height: string;
    minH: number;
    maxH: number;
  }
> = {
  default: { width: "9vw", minW: 100, maxW: 160, height: "5vw", minH: 56, maxH: 88 },
  compact: { width: "5vw", minW: 64, maxW: 96, height: "2.8vw", minH: 36, maxH: 54 },
};

export function XaesTitle({ size = "default" }: { size?: Size }) {
  const t = TITLE_SIZES[size];
  return (
    <div className="relative">
      {/* Scanline overlay drifts vertically — matches legacy intent. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)",
          animation: "xaes-scanline 8s linear infinite",
        }}
      />
      <div
        className="font-sans font-bold uppercase whitespace-nowrap"
        style={{
          fontSize: t.font,
          letterSpacing: t.letter,
          paddingLeft: t.padLeft,
          // Hardcoded warm-cream regardless of theme — the entry
          // sequence reads as a dark cinematic moment in both modes,
          // matching the vanilla `#FFF8EC` literal.
          color: "#FFF8EC",
          textShadow:
            "0 0 8px rgba(255,248,236,0.9), 0 0 20px rgba(var(--accent-sky-rgb),0.7), 0 0 45px rgba(var(--accent-true-blue-rgb),0.6), 0 0 80px rgba(var(--accent-true-blue-rgb),0.3)",
          animation: "xaes-title-glow 3s ease-in-out 1.8s infinite",
        }}
      >
        {LETTERS.map(({ char, flyFrom }) => (
          <span
            key={char}
            className="inline-block"
            style={
              {
                "--fly-from": flyFrom,
                animation:
                  "xaes-fly-in 2.2s cubic-bezier(0.16, 1, 0.3, 1) forwards",
              } as CSSProperties
            }
          >
            {char}
          </span>
        ))}
      </div>
    </div>
  );
}

export function PlasmaOrb({ size = "default" }: { size?: Size }) {
  const o = ORB_SIZES[size];
  return (
    <div
      className="flex items-center justify-center"
      style={{ animation: "xaes-fade-in 1.8s ease 0.3s both" }}
    >
      <div className="relative flex justify-center">
        <div
          aria-hidden
          className="pointer-events-none absolute"
          style={{
            width: "120%",
            height: "130%",
            borderRadius: "40%",
            background:
              "radial-gradient(ellipse, rgba(var(--accent-true-blue-rgb), 0.13) 0%, rgba(var(--accent-true-blue-rgb), 0.05) 55%, transparent 75%)",
            filter: "blur(10px)",
            animation: "xaes-orb-pulse 2.8s ease-in-out infinite",
          }}
        />
        <div
          className="relative block"
          style={{
            width: o.width,
            minWidth: o.minW,
            maxWidth: o.maxW,
            height: o.height,
            minHeight: o.minH,
            maxHeight: o.maxH,
            filter:
              "drop-shadow(0 0 7px rgba(var(--accent-true-blue-rgb), 0.5)) drop-shadow(0 0 18px rgba(var(--accent-true-blue-rgb), 0.2))",
          }}
        >
          <PlasmaCanvas
            className="block h-full w-full"
            ariaLabel="XAES plasma sphere"
          />
        </div>
      </div>
    </div>
  );
}
